import { HttpError } from '~/lib/useRequest/errors';

export function getApiErrorMessage(error: unknown, fallbackMessage = 'An unexpected error occurred.') {
  if (error instanceof HttpError) {
    const body = error.body;

    // Body as plain string
    if (typeof body === 'string' && body.length > 0) {
      return body;
    }

    const detailFromBody = extractDetailFromBody(body);
    if (detailFromBody) {
      return detailFromBody;
    }

    if (typeof error.message === 'string' && error.message.length > 0) {
      return error.message;
    }
  } else if (error instanceof Error && typeof error.message === 'string' && error.message.length > 0) {
    return error.message;
  }

  return fallbackMessage;
}

function extractDetailFromBody(body: unknown): string | undefined {
  if (!body || typeof body !== 'object') {
    return undefined;
  }

  // json:api error array at top-level
  const topErrors = (body as { errors?: unknown }).errors;
  const detailFromErrors = extractDetailFromErrors(topErrors);
  if (detailFromErrors) return detailFromErrors;

  const detail = (body as { detail?: unknown }).detail;
  if (typeof detail === 'string' && detail.length > 0) {
    return detail;
  }

  const message = (body as { message?: unknown }).message;
  if (typeof message === 'string' && message.length > 0) {
    return message;
  }

  return undefined;
}

function extractDetailFromErrors(errors: unknown): string | undefined {
  if (!Array.isArray(errors)) {
    return undefined;
  }

  for (const err of errors) {
    if (!err || typeof err !== 'object') continue;
    const nestedMetaDetail = extractDetailFromMeta((err as { meta?: unknown }).meta);
    if (nestedMetaDetail) {
      return nestedMetaDetail;
    }

    const detail = (err as { detail?: unknown }).detail;
    if (typeof detail === 'string' && detail.length > 0) {
      return detail;
    }
    const title = (err as { title?: unknown }).title;
    if (typeof title === 'string' && title.length > 0) {
      return title;
    }
  }

  return undefined;
}

function extractDetailFromMeta(meta: unknown): string | undefined {
  if (!meta || typeof meta !== 'object') {
    return undefined;
  }

  const nestedMetaDetail = (meta as { detail?: unknown }).detail;
  if (typeof nestedMetaDetail === 'string' && nestedMetaDetail.length > 0) {
    return nestedMetaDetail;
  }

  const errorData = (meta as { errorData?: unknown }).errorData;
  if (errorData) {
    if (typeof errorData === 'string') {
      try {
        const parsed = JSON.parse(errorData);
        const parsedDetail = extractDetailFromMeta(parsed);
        if (parsedDetail) {
          return parsedDetail;
        }
      } catch {
        // ignore parse errors
      }
    } else if (typeof errorData === 'object') {
      const nestedErrors = (errorData as { errors?: unknown }).errors;
      const nestedDetail = extractDetailFromErrors(nestedErrors);
      if (nestedDetail) {
        return nestedDetail;
      }
    }
  }

  return undefined;
}
